import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import {
  ArrowRight,
  FloppyDisk,
  Trash,
  WarningCircle,
  CreditCard,
  CheckCircle,
  Download,
  Shield,
} from "@phosphor-icons/react";

const PLAN_LABEL = {
  trial: "Free trial",
  subscription: "Subscription · £19/mo",
  annual: "Annual subscription · £190/yr",
  one_off: "One-off · £97 paid",
  past_due: "Payment past due",
  cancelled: "Subscription cancelled",
  read_only: "Read-only (subscription ended)",
  none: "No plan yet",
};

export default function Account() {
  const { user, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [profile, setProfile] = useState({
    name: user?.name || "",
    business_name: user?.business_name || "",
    business_profile: {
      industry: user?.business_profile?.industry || "",
      employees: user?.business_profile?.employees || "",
      legal_entity: user?.business_profile?.legal_entity || "",
      years_trading: user?.business_profile?.years_trading || "",
      biggest_challenge: user?.business_profile?.biggest_challenge || "",
      vat_registered: user?.business_profile?.vat_registered || "",
    },
  });
  const [editingProfile, setEditingProfile] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState("");
  const [deletePw, setDeletePw] = useState("");
  // Iter41 — final "Are you sure?" modal shown after the form validates, so
  // users can't muscle-memory their way through account deletion. The modal
  // spells out the GDPR-driven irreversibility so nobody's surprised.
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);

  // After a Stripe checkout redirect lands on /account?upgrade=success&session_id=cs_...
  // we poll the status endpoint to flip the user's plan locally without waiting for the
  // webhook to land first.
  useEffect(() => {
    // Sync local profile state whenever the auth user updates — so post-
    // onboarding data (industry, employees, biggest_challenge etc.) shows
    // up here without the user having to reload the page. Without this
    // the initial useState above only runs once and freezes stale values.
    if (!user) return;
    setProfile({
      name: user.name || "",
      business_name: user.business_name || "",
      business_profile: {
        industry: user.business_profile?.industry || "",
        employees: user.business_profile?.employees || "",
        legal_entity: user.business_profile?.legal_entity || "",
        years_trading: user.business_profile?.years_trading || "",
        biggest_challenge: user.business_profile?.biggest_challenge || "",
        vat_registered: user.business_profile?.vat_registered || "",
      },
    });
  }, [user]);

  useEffect(() => {
    // Also nudge a fresh /auth/me on Account mount so business_profile
    // populated by onboarding is guaranteed to be current.
    if (refreshUser) refreshUser().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Deep-link support: `/account#your-plan` (from the trial banner) should
    // land the user directly on the billing card. React Router doesn't do
    // hash-scroll natively — small effect handles it. Slight delay lets the
    // billing card mount if user is redirected from another route.
    const hash = location.hash;
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [location.hash]);

  useEffect(() => {
    const session_id = params.get("session_id");
    const upgrade = params.get("upgrade");
    if (!session_id || upgrade !== "success") return;
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/billing/status/${session_id}`);
        if (res.data?.payment_status === "paid") {
          toast.success("Payment received. Welcome aboard!");
          if (refreshUser) await refreshUser();
          // Strip the query params so refreshing doesn't re-poll.
          setParams({});
          return;
        }
      } catch {}
      if (!cancelled && attempts < 12) {
        setTimeout(poll, 1500);
      } else if (!cancelled) {
        toast.message("Still processing — refresh in a minute to see your plan.");
        setParams({});
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("session_id")]);

  const openPortal = async () => {
    setBusy(true);
    // Open a fresh tab up-front (before the awaited call) — browsers block
    // window.open() called after an await when it's not part of a user
    // gesture. We navigate the placeholder tab to the portal URL once we
    // have it, keeping this tab intact so nothing feels like a logout.
    const portalTab = window.open("about:blank", "_blank");
    try {
      const res = await axios.post(`${API}/billing/portal`, { origin_url: window.location.origin });
      if (res.data?.url) {
        if (portalTab && !portalTab.closed) portalTab.location.href = res.data.url;
        else window.open(res.data.url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      if (portalTab && !portalTab.closed) portalTab.close();
      toast.error(e?.response?.data?.detail || "Could not open Stripe portal");
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/auth/profile`, profile);
      toast.success("Profile updated.");
      setEditingProfile(false);
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (pw.new_password !== pw.confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async () => {
    if (confirmDelete !== "DELETE") return;
    if (!deletePw) {
      toast.error("Enter your current password to confirm.");
      return;
    }
    // Iter41 — open final modal instead of firing straight to the server.
    setShowFinalDeleteConfirm(true);
  };

  const performDelete = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/account/delete-me`, {
        password: deletePw,
        confirmation: "DELETE",
      });
      toast.success(
        "Your account has been deleted. A confirmation email is on its way.",
      );
      logout();
      // Hard navigate so all in-memory state + axios defaults get wiped.
      window.location.href = "/";
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) toast.error("Password incorrect. Please try again.");
      else toast.error(e?.response?.data?.detail || "Could not delete account.");
      setShowFinalDeleteConfirm(false);
    } finally {
      setBusy(false);
    }
  };

  // GDPR Article 20 — download all personal data we hold about this user.
  const exportMyData = async (format) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/account/export-my-data?format=${format}`, {
        responseType: "blob",
      });
      // Pull the filename from Content-Disposition if present, fall back to a sensible default.
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m ? m[1] : `forecastly-data.${format === "zip" ? "zip" : "json"}`;
      const blob = new Blob([res.data], {
        type: format === "zip" ? "application/zip" : "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(
        format === "zip" ? "CSV bundle downloaded." : "JSON file downloaded.",
      );
    } catch {
      toast.error("Could not export your data — please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-10 max-w-3xl">
      <div>
        <div className="section-eyebrow">Account settings</div>
        <h1 className="font-heading uppercase text-4xl md:text-5xl tracking-wide mt-4 leading-[1.05]">
          Manage your account
        </h1>
        <p className="mt-3 text-black/70 leading-relaxed">
          Update your profile, change your password, view your subscription, or delete your account
          here.
        </p>
      </div>

      {/* Profile */}
      <div className="card" data-testid="account-profile-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-heading uppercase text-2xl">Profile</h2>
          {!editingProfile && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setEditingProfile(true)}
              data-testid="account-profile-edit"
            >
              <FloppyDisk size={14} weight="bold" /> Edit
            </button>
          )}
        </div>

        {editingProfile ? (
          <>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold mb-1">Your name</label>
                <input
                  className="input"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  data-testid="account-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Business name</label>
                <input
                  className="input"
                  value={profile.business_name}
                  onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                  data-testid="account-business"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Legal entity</label>
                <select
                  className="input"
                  value={profile.business_profile.legal_entity}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      business_profile: { ...profile.business_profile, legal_entity: e.target.value },
                    })
                  }
                  data-testid="account-legal-entity"
                >
                  <option value="">Select…</option>
                  <option value="sole_trader">Sole Trader</option>
                  <option value="ltd">Limited Company (Ltd)</option>
                  <option value="partnership">Partnership</option>
                  <option value="cic">Community Interest Company (CIC)</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Number of people in your business</label>
                <select
                  className="input"
                  value={profile.business_profile.employees}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      business_profile: { ...profile.business_profile, employees: e.target.value },
                    })
                  }
                  data-testid="account-employees"
                >
                  <option value="">Select…</option>
                  <option value="0">Just me</option>
                  <option value="1">Me + 1 employee</option>
                  <option value="2-5">Me + 2–5 employees</option>
                  <option value="6-20">Me + 6–20 employees</option>
                  <option value="21+">Me + 21 or more employees</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Are you VAT registered?</label>
                <select
                  className="input"
                  value={profile.business_profile.vat_registered}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      business_profile: { ...profile.business_profile, vat_registered: e.target.value },
                    })
                  }
                  data-testid="account-vat"
                >
                  <option value="">Select…</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Years trading</label>
                <select
                  className="input"
                  value={profile.business_profile.years_trading}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      business_profile: { ...profile.business_profile, years_trading: e.target.value },
                    })
                  }
                  data-testid="account-years"
                >
                  <option value="">Select…</option>
                  <option value="just_starting">Just starting out</option>
                  <option value="under_1">Less than 1 year</option>
                  <option value="1_3">1–3 years</option>
                  <option value="3_5">3–5 years</option>
                  <option value="5_plus">5+ years</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Industry / type of business</label>
                <input
                  className="input"
                  value={profile.business_profile.industry}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      business_profile: { ...profile.business_profile, industry: e.target.value },
                    })
                  }
                  data-testid="account-industry"
                  maxLength={200}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold mb-1">Biggest challenge right now</label>
                <textarea
                  className="input min-h-[80px] py-2"
                  value={profile.business_profile.biggest_challenge}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      business_profile: {
                        ...profile.business_profile,
                        biggest_challenge: e.target.value,
                      },
                    })
                  }
                  data-testid="account-challenge"
                  maxLength={500}
                />
                <p className="text-xs text-black/55 mt-1">
                  Feeds every AI insight so the story stays relevant to what you're wrestling with.
                </p>
              </div>
            </div>
            <div className="mt-3 text-sm text-black/55">
              Email: <strong>{user?.email}</strong>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                className="btn-primary"
                onClick={saveProfile}
                disabled={busy}
                data-testid="account-save-profile"
              >
                <FloppyDisk size={16} weight="bold" /> {busy ? "Saving…" : "Save profile"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setEditingProfile(false);
                  // Reset any unsaved changes
                  setProfile({
                    name: user?.name || "",
                    business_name: user?.business_name || "",
                    business_profile: {
                      industry: user?.business_profile?.industry || "",
                      employees: user?.business_profile?.employees || "",
                      legal_entity: user?.business_profile?.legal_entity || "",
                      years_trading: user?.business_profile?.years_trading || "",
                      biggest_challenge: user?.business_profile?.biggest_challenge || "",
      vat_registered: user?.business_profile?.vat_registered || "",
                    },
                  });
                }}
                data-testid="account-cancel-profile"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm" data-testid="account-profile-readonly">
              <ProfileReadRow label="Your name" value={user?.name} />
              <ProfileReadRow label="Business name" value={user?.business_name} />
              <ProfileReadRow
                label="Legal entity"
                value={
                  {
                    sole_trader: "Sole Trader",
                    ltd: "Limited Company (Ltd)",
                    partnership: "Partnership",
                    cic: "Community Interest Company (CIC)",
                    other: "Other",
                  }[user?.business_profile?.legal_entity] || null
                }
              />
              <ProfileReadRow
                label="Number of people in your business"
                value={
                  {
                    "0": "Just me",
                    "1": "Me + 1 employee",
                    "2-5": "Me + 2–5 employees",
                    "6-20": "Me + 6–20 employees",
                    "21+": "Me + 21 or more employees",
                  }[user?.business_profile?.employees] || null
                }
              />
              <ProfileReadRow
                label="VAT registered"
                value={{ yes: "Yes", no: "No" }[user?.business_profile?.vat_registered] || null}
              />
              <ProfileReadRow
                label="Years trading"
                value={
                  {
                    just_starting: "Just starting out",
                    under_1: "Less than 1 year",
                    "1_3": "1–3 years",
                    "3_5": "3–5 years",
                    "5_plus": "5+ years",
                  }[user?.business_profile?.years_trading] || null
                }
              />
              <ProfileReadRow label="Industry" value={user?.business_profile?.industry} />
              <div className="sm:col-span-2">
                <div className="text-xs uppercase tracking-widest text-black/55 font-semibold">
                  Biggest challenge right now
                </div>
                <div className="mt-1 text-black/85">
                  {user?.business_profile?.biggest_challenge || (
                    <span className="text-black/40">— not set —</span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 text-sm text-black/55">
              Email: <strong>{user?.email}</strong>
            </div>
          </>
        )}
      </div>

      {/* Plan / billing card */}
      <div className="card scroll-mt-24" id="your-plan" data-testid="plan-card">
        <h2 className="font-heading uppercase text-2xl flex items-center gap-2">
          <CreditCard size={22} weight="duotone" /> Your plan
        </h2>

        {user?.vip_lifetime ? (
          // VIP LIFETIME — no billing to manage, no chip, no button. Warm copy.
          <>
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="chip bg-[#FFBD4A] border-black" data-testid="plan-vip-chip">
                VIP · lifetime
              </span>
            </div>
            <p className="mt-3 text-sm text-black/75 leading-relaxed max-w-2xl">
              You're a forecastly VIP — full access, forever. No billing to manage,
              nothing to renew. Enjoy every feature on us.
            </p>
          </>
        ) : (
          <>
            <div className="mt-3 flex items-center gap-2 flex-wrap text-sm">
              <span className="chip" data-testid="plan-label">
                {PLAN_LABEL[user?.plan] || PLAN_LABEL.none}
              </span>
              {user?.vip_until && (
                <span className="chip bg-[#FFBD4A] border-black" data-testid="plan-vip-until">
                  Free access until {new Date(user.vip_until).toLocaleDateString("en-GB")}
                </span>
              )}
              {user?.plan === "trial" && user?.trial_ends_at && (
                <span className="chip bg-black text-[#FFBD4A]" data-testid="plan-trial-ends">
                  Trial ends {new Date(user.trial_ends_at).toLocaleDateString("en-GB")}
                </span>
              )}
            </div>
            <p className="mt-3 text-sm text-black/65 leading-relaxed max-w-2xl">
              {user?.plan === "trial" &&
                "You're on your 10-day free trial. Full access to every feature — no charge until the trial ends. Add or update your card any time from the button below."}
              {user?.plan === "subscription" &&
                "You're on the monthly subscription. Use the buttons below to update your card, download invoices, or cancel — all handled securely on Stripe."}
              {user?.plan === "annual" &&
                "You're on the annual subscription. Use the buttons below to update your card, download invoices, or cancel — all handled securely on Stripe."}
              {user?.plan === "past_due" &&
                "Your last payment didn't go through. Update your card below to keep full access — otherwise your account will drop to read-only shortly."}
              {user?.plan === "one_off" &&
                "You bought the one-off forecast. Upgrade to the subscription anytime to unlock actuals tracking, AI insights, and goals coaching."}
              {user?.plan === "cancelled" &&
                "Your subscription is cancelled. Your historic actuals and AI insights are still here, read-only. Reactivate anytime to keep tracking month by month."}
              {user?.plan === "read_only" &&
                "Your subscription ended. Your data is safe and read-only. Reactivate to start tracking again."}
              {(!user?.plan || user?.plan === "none") &&
                "You don't have an active plan. Pick one below to get full access."}
            </p>

            {/* Iter37 — offer confirmation for URL-campaign signups. Shown during
                trial AND permanently after so users can always verify the offer
                they claimed at signup.
                Iter38 — same card now also renders for referral signups (no
                campaign slug, but `referred_by_code` present on the user). */}
            {(user?.signup_campaign_slug || user?.referred_by_code) && (
              <OfferConfirmation user={user} />
            )}

            <div className="mt-5 flex flex-wrap gap-3">
              {(user?.has_subscription_id ||
                user?.plan === "subscription" ||
                user?.plan === "annual" ||
                user?.plan === "trial" ||
                user?.plan === "past_due" ||
                user?.plan === "cancelled") && (
                <button
                  className="btn-secondary"
                  onClick={openPortal}
                  disabled={busy}
                  data-testid="manage-subscription-btn"
                >
                  <CreditCard size={16} weight="bold" /> Manage billing & invoices
                </button>
              )}
              {user?.plan === "one_off" && (
                <Link
                  to="/upgrade"
                  className="btn-primary"
                  data-testid="account-upgrade-btn"
                >
                  Upgrade to subscription <ArrowRight size={16} weight="bold" />
                </Link>
              )}
              {(user?.plan === "cancelled" || user?.plan === "read_only") && (
                <Link
                  to="/upgrade"
                  className="btn-primary"
                  data-testid="account-reactivate-btn"
                >
                  Reactivate subscription <ArrowRight size={16} weight="bold" />
                </Link>
              )}
              {(!user?.plan || user?.plan === "none") && (
                <Link
                  to="/upgrade"
                  className="btn-primary"
                  data-testid="account-pick-plan-btn"
                >
                  Pick a plan <ArrowRight size={16} weight="bold" />
                </Link>
              )}
            </div>
            {(user?.plan === "subscription" || user?.plan === "annual") && (
              <p className="mt-4 text-xs text-black/55 leading-relaxed">
                "Manage billing" opens your private Stripe customer portal — change your card,
                download VAT-ready invoices, pause, or cancel. Cancellations take effect at the end
                of the current period; your data stays.
              </p>
            )}
          </>
        )}
      </div>

      {/* VIP grant card removed (Iter30) — Admin page is now the single source
          of truth for granting/revoking VIP + creating manual users. */}


      {/* Password */}
      <div className="card">
        <h2 className="font-heading uppercase text-2xl">Change password</h2>
        <form onSubmit={changePassword} className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">Current password</label>
            <input
              type="password"
              className="input"
              value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              required
              data-testid="account-current-pw"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">New password</label>
              <input
                type="password"
                className="input"
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                required
                minLength={6}
                data-testid="account-new-pw"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Confirm new password</label>
              <input
                type="password"
                className="input"
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                required
                data-testid="account-confirm-pw"
              />
            </div>
          </div>
          <button className="btn-primary" disabled={busy} data-testid="account-change-pw">
            Update password <ArrowRight size={16} weight="bold" />
          </button>
        </form>
      </div>

      {/* Your data & privacy — GDPR rights */}
      <div className="card" data-testid="account-data-privacy-card">
        <h2 className="font-heading uppercase text-2xl flex items-center gap-2">
          <Shield size={22} weight="duotone" /> Your data & privacy
        </h2>
        <p className="mt-3 text-black/80 leading-relaxed">
          Under UK GDPR you have the right to a copy of every piece of personal data
          we hold about you. Download it in whichever format suits — JSON for tools,
          a ZIP of CSVs if you'd rather open it in Excel.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => exportMyData("zip")}
            disabled={exporting}
            data-testid="account-export-csv-btn"
          >
            <Download size={16} weight="bold" />
            {exporting ? "Preparing…" : "Download as CSVs (zip)"}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => exportMyData("json")}
            disabled={exporting}
            data-testid="account-export-json-btn"
          >
            <Download size={16} weight="bold" />
            {exporting ? "Preparing…" : "Download as JSON"}
          </button>
        </div>
        <p className="mt-3 text-xs text-black/55 leading-relaxed">
          Read our{" "}
          <Link to="/privacy" className="underline font-semibold">
            Privacy Policy
          </Link>{" "}
          for the full detail of what we collect, why, and how long we keep it.
        </p>
      </div>

      {/* Danger zone */}
      <div className="card border-red-700/40" data-testid="account-danger-zone">
        <h2 className="font-heading uppercase text-2xl flex items-center gap-2 text-red-700">
          <WarningCircle size={22} weight="duotone" /> Danger zone
        </h2>
        <p className="mt-3 text-black/80 leading-relaxed">
          Deleting your account is <strong>permanent and irreversible</strong>. We will:
        </p>
        <ul className="mt-2 ml-5 list-disc text-sm text-black/80 leading-relaxed space-y-1">
          <li>Erase your forecast, actuals, goals, and account profile from our database.</li>
          <li>Cancel any active Stripe subscription so you won't be charged again.</li>
          <li>Remove your email address from our MailerLite list.</li>
          <li>Send you a one-line confirmation email.</li>
        </ul>
        <p className="mt-3 text-sm text-black/80">
          Tax records (payment receipts only) are retained for 6 years as required by
          HMRC. Type <strong>DELETE</strong> below and enter your current password
          to confirm.
        </p>
        <div className="mt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-center">
          <input
            className="input"
            placeholder="Type DELETE to confirm"
            value={confirmDelete}
            onChange={(e) => setConfirmDelete(e.target.value)}
            data-testid="account-delete-confirm"
          />
          <input
            type="password"
            className="input"
            placeholder="Your current password"
            value={deletePw}
            onChange={(e) => setDeletePw(e.target.value)}
            data-testid="account-delete-pw"
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={deleteAccount}
            disabled={busy || confirmDelete !== "DELETE" || !deletePw}
            data-testid="account-delete-btn"
            className="btn-secondary border-red-700 text-red-700 disabled:opacity-50 whitespace-nowrap"
          >
            <Trash size={16} weight="bold" /> Delete my account
          </button>
        </div>
      </div>

      <div>
        <Link to="/dashboard" className="text-sm font-semibold underline">
          ← Back to dashboard
        </Link>
      </div>

      {/* Iter41 — final "Are you sure?" modal. Only shown once the user has
          already typed DELETE + password. Wording spells out that hard-delete
          is a GDPR requirement so nobody's blindsided by no-way-back. */}
      {showFinalDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-testid="account-delete-final-modal"
        >
          <div className="bg-white border-[1.5px] border-black rounded-2xl max-w-md w-full p-6 space-y-5">
            <div className="flex items-start gap-3">
              <WarningCircle size={28} weight="duotone" className="shrink-0 mt-1 text-red-700" />
              <div>
                <h3 className="font-heading uppercase text-2xl leading-tight">
                  Last check — are you sure?
                </h3>
              </div>
            </div>
            <div className="space-y-3 text-sm text-black/80 leading-relaxed">
              <p>
                This is your <strong>final chance to change your mind</strong>. Tap
                Delete and every forecast, actual, goal and profile field we hold about
                you disappears — instantly and forever.
              </p>
              <p>
                Under UK GDPR (Article 17, the "right to erasure") we <strong>permanently
                wipe</strong> your data rather than soft-delete it. There is no
                reactivation window. If you want to come back later, you'll need to sign
                up fresh.
              </p>
              <p className="text-black/65">
                Payment receipts stay archived for 6 years as HMRC requires — everything
                else is gone.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  // Iter41 code-review fix — clearing the outer form on Cancel
                  // prevents a stray double-click on 'Delete my account' from
                  // reopening the modal without a fresh consent step.
                  setShowFinalDeleteConfirm(false);
                  setConfirmDelete("");
                  setDeletePw("");
                }}
                disabled={busy}
                className="btn-secondary flex-1 min-w-[130px]"
                data-testid="account-delete-final-cancel"
              >
                Cancel — keep my account
              </button>
              <button
                type="button"
                onClick={performDelete}
                disabled={busy}
                className="btn-secondary border-red-700 text-red-700 flex-1 min-w-[130px] whitespace-nowrap"
                data-testid="account-delete-final-confirm"
              >
                <Trash size={16} weight="bold" /> {busy ? "Deleting…" : "Yes, delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Iter37 — small confirmation card shown on the Account page when the user
// signed up via a URL campaign. Fetches the campaign meta so we can show a
// human summary. Computes concrete dates locally so the numbers match what
// they saw in the welcome email + on Stripe checkout.
// Iter38 — also handles referral signups (no campaign slug, but `referred_by_code`
// present). Falls back to the referrer's first name + fixed 30-day bonus wording.
function OfferConfirmation({ user }) {
  const [campaign, setCampaign] = React.useState(null);
  const isReferral = !user?.signup_campaign_slug && !!user?.referred_by_code;

  React.useEffect(() => {
    if (!user?.signup_campaign_slug) return;
    axios
      .get(`${API}/campaigns/${user.signup_campaign_slug}`)
      .then((r) => setCampaign(r.data))
      .catch(() => setCampaign({ human_summary: "Special offer applied" }));
  }, [user?.signup_campaign_slug]);

  const trialStart = user?.trial_started_at ? new Date(user.trial_started_at) : null;
  const trialEnd = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const anchorDays = Number(user?.signup_anchor_shift_days || campaign?.anchor_shift_days || 0);
  const renewal =
    trialEnd && anchorDays
      ? new Date(trialEnd.getTime() + (365 + anchorDays) * 86400_000)
      : null;

  const fmt = (d) =>
    d ? d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

  // Compose the display bits depending on the offer type.
  const referrerFirst = (user?.referrer_first_name || "").trim();
  const bonusDays = Number(user?.referral_trial_bonus_days || 30);
  const eyebrow = isReferral ? "Your friend's gift" : "Your signup offer";
  const headline = isReferral
    ? (referrerFirst
        ? `${referrerFirst.charAt(0).toUpperCase()}${referrerFirst.slice(1)} gifted you ${bonusDays} extra days on us`
        : `A friend gifted you ${bonusDays} extra days on us`)
    : (campaign?.human_summary || "Special offer applied");

  return (
    <div
      className="mt-4 p-4 rounded-xl border-[1.5px] border-black bg-[#FFF7E5]"
      data-testid={isReferral ? "account-referral-confirmation" : "account-offer-confirmation"}
    >
      <div className="flex items-start gap-2">
        <span className="text-lg leading-none pt-0.5">🎁</span>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-heading text-black/70">
            {eyebrow}
          </div>
          <div className="font-semibold text-sm mt-0.5">
            {headline}
            {user?.signup_campaign_slug && (
              <span className="ml-2 text-xs font-mono text-black/55">
                ({user.signup_campaign_slug})
              </span>
            )}
          </div>
          <div className="mt-2 grid sm:grid-cols-3 gap-3 text-xs">
            {trialStart && (
              <div>
                <div className="text-black/55">Trial started</div>
                <div className="font-semibold">{fmt(trialStart)}</div>
              </div>
            )}
            {trialEnd && (
              <div>
                <div className="text-black/55">First charge</div>
                <div className="font-semibold">{fmt(trialEnd)}</div>
              </div>
            )}
            {renewal && (
              <div>
                <div className="text-black/55">Next renewal</div>
                <div className="font-semibold">~{fmt(renewal)}</div>
              </div>
            )}
          </div>
          {user?.signup_anchor_shift_applied_at && (
            <p className="mt-2 text-[11px] text-emerald-700 font-semibold">
              ✓ Free months applied to your billing cycle
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


function ProfileReadRow({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-black/55 font-semibold">{label}</div>
      <div className="mt-1 text-black/85">
        {value || <span className="text-black/40">— not set —</span>}
      </div>
    </div>
  );
}
